// Always-visible allocation strip: next month's engineer-months per discipline,
// live-updated as orders are queued. The answer to "can I afford this stepper?"
// Garage months have no payroll — the strip shows founder-months instead.

import type { ContentPack, GarageActivity } from "../../engine/types.js";
import { GARAGE_ACTIVITIES } from "../../engine/types.js";
import type { SessionSnapshot } from "../session.js";
import { projectNextMonth, type DisciplineProjection } from "../allocation.js";

const LABEL = { design: "DESIGN", verification: "VERIF", implementation: "IMPL" } as const;

export function AllocationStrip({ snap, content }: { snap: SessionSnapshot; content: ContentPack }) {
  if (snap.view.stage === "garage" && snap.view.garage) return <GarageStrip snap={snap} content={content} />;
  const proj = projectNextMonth(snap.view, snap.pending, content);
  return (
    <div className="alloc">
      <span className="k">NEXT MONTH'S ENG-MONTHS (orders included) →</span>
      {(["design", "verification", "implementation"] as const).map((d) => (
        <Cell key={d} label={LABEL[d]} p={proj[d]} />
      ))}
    </div>
  );
}

/** Founder-months ledger: the garage's one scarce pool (orders included). */
function GarageStrip({ snap, content }: { snap: SessionSnapshot; content: ContentPack }) {
  const g = snap.view.garage!;
  const queued = snap.pending.find((c) => c.type === "setGarageAlloc");
  const alloc: Partial<Record<GarageActivity, number>> =
    queued && queued.type === "setGarageAlloc" ? queued.alloc : g.alloc;
  const dayJob = g.savings <= 0;
  const available = g.founders.length * (dayJob ? content.economy.garage.dayJobEff : 1);
  const requested = GARAGE_ACTIVITIES.reduce((a, k) => a + (alloc[k] ?? 0), 0);
  const parts = GARAGE_ACTIVITIES.filter((k) => (alloc[k] ?? 0) > 0)
    .map((k) => `${k} ${fmt(alloc[k]!)}`)
    .join(" · ");
  return (
    <div className="alloc">
      <span className="k">NEXT MONTH'S FOUNDER-MONTHS (orders included) →</span>
      <span className="cell" title={parts || "nothing allocated — the clock burns anyway"}>
        <span className="k">FOUNDERS</span>
        <span className="bar" style={{ width: 72, display: "inline-block" }}>
          <span className="fill" style={{ width: `${available > 0 ? Math.min(100, (requested / available) * 100) : 0}%` }} />
        </span>
        <span>
          {fmt(Math.min(requested, available))}/{fmt(available)}
        </span>
        <span className={requested > available ? "idle-zero" : requested === 0 ? "idle-over" : "idle-ok"}>
          {requested > available
            ? `over-asked — scaled to ${fmt(available)}`
            : requested === 0
              ? "IDLE — savings burn anyway"
              : `${fmt(available - requested)} idle`}
        </span>
      </span>
      <span className="k">{parts}</span>
      {dayJob && <span className="idle-over">EVERYONE'S DAY-JOBBING — {fmt(content.economy.garage.dayJobEff)} fm each</span>}
    </div>
  );
}

function fmt(x: number): string {
  return Number.isInteger(x) ? String(x) : x.toFixed(1);
}

function Cell({ label, p }: { label: string; p: DisciplineProjection }) {
  const over = p.idle < -1e-9;
  const exact = !over && p.idle < 0.5;
  const parts = [
    p.nre > 0 ? `nre ${fmt(p.nre)}` : "",
    p.sustaining > 0 ? `sustain ${fmt(p.sustaining)}` : "",
    p.qual > 0 ? `qual ${fmt(p.qual)}` : "",
    p.research > 0 ? `yield ${fmt(p.research)}` : "",
    p.reliability > 0 ? `rel ${fmt(p.reliability)}` : "",
    p.tools > 0 ? `tools ${fmt(p.tools)}` : "",
    p.tree > 0 ? `tree ${fmt(p.tree)}` : "",
    p.projects > 0 ? `projects ${fmt(p.projects)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span className="cell" title={parts || "nothing allocated"}>
      <span className="k">{label}</span>
      <span className="bar" style={{ width: 72, display: "inline-block" }}>
        <span
          className={`fill${over ? " over" : ""}`}
          style={{ width: `${p.budget > 0 ? Math.min(100, (p.committed / p.budget) * 100) : p.committed > 0 ? 100 : 0}%` }}
        />
      </span>
      <span>
        {fmt(p.committed)}/{fmt(p.budget)}
      </span>
      <span className={over ? "idle-over" : exact ? "idle-zero" : "idle-ok"}>
        {over ? `OVER by ${fmt(-p.idle)}` : `${fmt(p.idle)} idle`}
      </span>
    </span>
  );
}
